import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * One shared socket.io connection to the backend's realtime gateway
 * (src/common/gateways/realtime.gateway.ts), authenticated with the same
 * JWT used for REST calls. The gateway connects to the tenant room and receives
 * emergency-code activations, resolutions, and realtime alerts.
 */
export function useRealtimeSocket(
  handlers: Record<string, (payload: any) => void>,
) {
  const socketRef = useRef<Socket | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const authContext = (useAuth() as any) || {};
  const token = !authContext.loading && authContext.user
    ? authContext.token || localStorage.getItem("auth_token") : null;

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || "http://localhost:3003";

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    console.log("[RealtimeSocket] Connecting to gateway:", baseUrl);

    // The REST prefix is not a Socket.IO namespace. The gateway uses '/'.
    const socket = io(new URL(baseUrl, window.location.origin).origin, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("[RealtimeSocket] Connected successfully! ID:", socket.id);
    });

    socket.on("disconnect", (reason) => {
      console.warn("[RealtimeSocket] Disconnected:", reason);
    });

    socket.on("connect_error", (err) => {
      console.warn("[RealtimeSocket] Connection error:", err.message);
    });

    const boundHandlers: Record<string, (payload: any) => void> = {};
    Object.keys(handlersRef.current).forEach((event) => {
      const wrapped = (payload: any) => {
        console.log(`[RealtimeSocket] Event received "${event}":`, payload);
        handlersRef.current[event]?.(payload);
      };
      boundHandlers[event] = wrapped;
      socket.on(event, wrapped);
    });

    return () => {
      Object.entries(boundHandlers).forEach(([event, fn]) => socket.off(event, fn));
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  return socketRef;
}
